import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Tabs, Tab, Form, Modal } from 'react-bootstrap';
import AgeCategoryTable from './AgeCategoryTable';
import TeamsTable from './TeamsTable';
import PlayersTable from './PlayersTable';
import StandingsTable from './StandingsTable';
import VotingManagement from './VotingManagement';
import PredictionsManagement from './PredictionsManagement';
import Breadcrumbs from '../../../Common/Breadcrumbs';
import CustomTabs from './CustomTabs';
import { editTournament, uploadTournamentLogo } from '../../../api/tournament/tournamentService';
import { getAllTournamentSeasons, getTournamentById, ManageTournament } from '../../../api/tournament/manageTournamentService';
import { getAllAgeCategoriesPaginated } from '../../../api/tournament/ageCategoryService';
import isEqual from 'lodash/isEqual';
import { format } from 'date-fns';
import { getSportValue as getSportValueUtil } from '../../../utils/getSportValue';
import Select from 'react-select';
import { listTeams, addTeam, editTeam, deleteTeam } from '../../../api/tournament/teamService';
import ShimmerLoader from '../../../Common/ShimmerLoader';
import { listPlayers, addPlayer, editPlayer, deletePlayer } from '../../../api/tournament/playerService';
import { getAllStandings, addStanding, updateStanding, deleteStanding } from '../../../api/tournament/standingsService';
import { getImageUrl } from '../../../utils/imageUrl';

type OptionType = { value: string; label: string };

const TournamentDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [tournament, setTournament] = useState<ManageTournament | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState<any>({});
  const [activeKey, setActiveKey] = useState('details');
  // Dummy data for tabs (replace with real API calls)
  const [ageCategories, setAgeCategories] = useState([]);
  const [teams, setTeams] = useState([]);
  const [players, setPlayers] = useState<any[]>([]);
  const [standings, setStandings] = useState<any[]>([]);
  const [ageCategorySearch, setAgeCategorySearch] = useState('');
  const [teamSearch, setTeamSearch] = useState('');
  const [playerSearch, setPlayerSearch] = useState('');
  const [standingsSearch, setStandingsSearch] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoError, setLogoError] = useState<string | null>(null);
  const [ageCategoriesLoading, setAgeCategoriesLoading] = useState(false);
  const [ageCategoriesError, setAgeCategoriesError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [formTouched, setFormTouched] = useState<any>({});
  const [originalEditValues, setOriginalEditValues] = useState<any>(null);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [teamsError, setTeamsError] = useState<string | null>(null);
  const [teamModalRefresh, setTeamModalRefresh] = useState(false); // to trigger refresh after add/edit/delete
  const [loading, setLoading] = useState(true);
  const [defaultSeasonId, setDefaultSeasonId] = useState<string | undefined>();

  // Players tab state
  const [playersLoading, setPlayersLoading] = useState(false);
  const [playersError, setPlayersError] = useState<string | null>(null);
  const [playersPage, setPlayersPage] = useState(1);
  const [playersPageSize, setPlayersPageSize] = useState(10);
  const [playersTotalPages, setPlayersTotalPages] = useState(1);
  const [playersTotalCount, setPlayersTotalCount] = useState(0);

  // Standings tab state
  const [standingsLoading, setStandingsLoading] = useState(false);
  const [standingsError, setStandingsError] = useState<string | null>(null);
  const [standingsPage, setStandingsPage] = useState(1);
  const [standingsPageSize, setStandingsPageSize] = useState(10);
  const [standingsTotalPages, setStandingsTotalPages] = useState(1);
  const [standingsTotalCount, setStandingsTotalCount] = useState(0);
  // Team/age category options for player modal
  const [teamOptions, setTeamOptions] = useState<{ value: string; label: string }[]>([]);
  const [ageCategoryOptions, setAgeCategoryOptions] = useState<{ value: string; label: string }[]>([]);
  const [playersTeamFilter, setPlayersTeamFilter] = useState<OptionType | null>(null);
  const [playersAgeCategoryFilter, setPlayersAgeCategoryFilter] = useState<OptionType | null>(null);
  const [standingsAgeCategoryFilter, setStandingsAgeCategoryFilter] = useState<OptionType | null>(null);
  const [standingsGroupFilter, setStandingsGroupFilter] = useState<OptionType | null>(null);

  // Age category pagination state
  const [ageCategoryPage, setAgeCategoryPage] = useState(1);
  const [ageCategoryPageSize, setAgeCategoryPageSize] = useState(10);
  const [ageCategoryTotalPages, setAgeCategoryTotalPages] = useState(1);
  const [ageCategoryTotalCount, setAgeCategoryTotalCount] = useState(0);

  // Teams pagination state
  const [teamsPage, setTeamsPage] = useState(1);
  const [teamsPageSize, setTeamsPageSize] = useState(10);
  const [teamsTotalPages, setTeamsTotalPages] = useState(1);
  const [teamsTotalCount, setTeamsTotalCount] = useState(0);
  const [teamsAgeCategoryFilter, setTeamsAgeCategoryFilter] = useState<OptionType | null>(null);

  const [playersRefresh, setPlayersRefresh] = useState(false);
  const [ageCategoryRefresh, setAgeCategoryRefresh] = useState(false);
  const [standingsRefresh, setStandingsRefresh] = useState(false);

  // Add state for edit modal and selected age category
  const [showAgeCategoryModal, setShowAgeCategoryModal] = useState(false);
  const [editAgeCategoryIndex, setEditAgeCategoryIndex] = useState<number | null>(null);

  // New state for filters
  const [typeFilter, setTypeFilter] = useState(null);
  const [formatFilter, setFormatFilter] = useState(null);
  // Dynamic options based on actual data
  const [typeOptions, setTypeOptions] = useState([
    { value: 'single', label: 'Single' },
    { value: 'group', label: 'Group' },
    { value: 'none', label: 'None' },
  ]);
  const [formatOptions, setFormatOptions] = useState([
    { value: 'team', label: 'Team' },
    { value: 'player', label: 'Player' },
  ]);

  useEffect(() => {
    if (id) {
      setLoading(true);
      console.log('Fetching tournament with ID:', id);
      // Get sport from location state or URL search params
      // Sport is REQUIRED for tournament management module
      const location = window.location;
      const urlParams = new URLSearchParams(location.search);
      const sportFromUrl = urlParams.get('sport');
      const selectedTournamentFromState = (window.history.state?.usr as any)?.selectedTournament;
      const sportFromState = selectedTournamentFromState?.sports;
      const sport = sportFromState || sportFromUrl || 'Football'; // Default to Football if not available
      
      getTournamentById(id, sport).then(async data => {
        console.log('Tournament data received:', data);
        setTournament(data);
        setForm(data); // Set form when tournament is fetched
        try {
          const allSeasons = await getAllTournamentSeasons(id, data.sports || sport);
          const defaultSeason = allSeasons.find((season: any) => season.seasonType === 'DEFAULT');
          const firstSeason = allSeasons.find((season: any) => season.seasonType === 'SEASON');
          setDefaultSeasonId(defaultSeason?._id || firstSeason?._id);
        } catch (seasonError) {
          console.warn('Could not fetch tournament seasons:', seasonError);
          setDefaultSeasonId(undefined);
        }
        setLoading(false);
      }).catch(err => {
        console.error('Error fetching tournament:', err);
        console.error('Tournament ID that failed:', id);
        setLoading(false);
        // Set a default tournament object to prevent crashes
        const defaultTournament: ManageTournament = {
          _id: id,
          name: 'Tournament Not Found',
          shortName: 'N/A',
          status: 'inactive',
          logo: '',
          location: '',
          startDate: '',
          endDate: '',
          sports: '',
          ageCategories: []
        };
        setTournament(defaultTournament);
        setForm(defaultTournament);
        setDefaultSeasonId(undefined);
      });
    }
  }, [id]);

  useEffect(() => {
    console.log('tournament:', tournament);
    console.log('form:', form);
  }, [tournament, form]);

  useEffect(() => {
    console.log('activeKey:', activeKey, 'tournament:', tournament);
    if (activeKey === 'age-category' && tournament && (tournament._id)) {
      setAgeCategoriesLoading(true);
      setAgeCategoriesError(null);
      getAllAgeCategoriesPaginated(tournament._id, ageCategoryPage, ageCategoryPageSize, '', '', '', undefined, tournament.sports || getSportValueUtil(tournament, { state: window.history.state?.usr, search: window.location.search }))
        .then(data => {
          console.log('Age categories API response:', data);
          const arr = data?.paginatedResults || [];
          console.log('Age categories array:', arr);
          const mapped = arr.map((ac: any) => {
            console.log('Mapping age category:', ac);
            return {
              ...ac,
              format: ac.format || 'player',
              standingsType: ac.standingsType || 'none',
              liveImage: ac.liveImage || null,
              highlightsImage: ac.highlightsImage || null,
              tournament: ac.tournament || {
                _id: tournament._id,
                name: tournament.name || '',
                shortName: tournament.shortName || ''
              }
            };
          });
          console.log('Mapped age categories:', mapped);
          setAgeCategories(mapped);
          setAgeCategoryTotalPages(data?.totalPages || 1);
          setAgeCategoryTotalCount(data?.totalCount || 0);
          setAgeCategoriesLoading(false);
        })
        .catch(() => {
          setAgeCategoriesError('Failed to load age categories.');
          setAgeCategoriesLoading(false);
        });
    }
  }, [activeKey, tournament, ageCategoryPage, ageCategoryPageSize]);

  // Fetch teams when Teams tab is active
  useEffect(() => {
    if (activeKey === 'teams' && tournament && (tournament._id)) {
      setTeamsLoading(true);
      setTeamsError(null);
      const ageCategoryValue = teamsAgeCategoryFilter?.value || '';
      console.log('Teams API call with params:', {
        page: teamsPage,
        pageSize: teamsPageSize,
        tournamentId: tournament._id,
        search: teamSearch,
        ageCategory: ageCategoryValue
      });
      listTeams({ 
        page: teamsPage, 
        pageSize: teamsPageSize, 
        tournamentId: tournament._id, 
        search: teamSearch,
        ageCategory: ageCategoryValue,
        sport: tournament.sports || getSportValueUtil(tournament, { state: window.history.state?.usr, search: window.location.search }) // FIX: Pass sport parameter
      })
        .then(data => {
          const arr = data?.paginatedResults || [];
          setTeams(arr);
          setTeamsTotalPages(data?.totalPages || 1);
          setTeamsTotalCount(data?.totalCount || 0);
          setTeamsLoading(false);
        })
        .catch(() => {
          setTeamsError('Failed to load teams.');
          setTeamsLoading(false);
        });
    }
  }, [activeKey, tournament, teamModalRefresh, teamsPage, teamsPageSize, teamSearch, teamsAgeCategoryFilter]);

  // Reset to page 1 on team search change
  useEffect(() => {
    setTeamsPage(1);
  }, [teamSearch]);

  // Reset to page 1 on team age category filter change
  useEffect(() => {
    setTeamsPage(1);
  }, [teamsAgeCategoryFilter]);


  // Fetch age categories when Age Category tab is active
  useEffect(() => {
    if (activeKey === 'age-category' && tournament && (tournament._id)) {
      setAgeCategoriesLoading(true);
      setAgeCategoriesError(null);
      getAllAgeCategoriesPaginated(tournament._id, ageCategoryPage, ageCategoryPageSize, ageCategorySearch, '', '', undefined, tournament.sports || 'Football')
        .then(data => {
          console.log('Age categories API response:', data); // Debugging
          const arr = data?.paginatedResults || [];
          console.log('Age categories array:', arr); // Debugging
          const mapped = arr.map((ac: any) => {
            console.log('Mapping age category:', ac); // Debugging
            return {
              ...ac,
              format: ac.format || 'player',
              standingsType: ac.standingsType || 'none',
              liveImage: ac.liveImage || null, // Changed from '' to null
              highlightsImage: ac.highlightsImage || null, // Changed from '' to null
              tournament: ac.tournament || {
                _id: tournament._id,
                name: tournament.name || '',
                shortName: tournament.shortName || ''
              }
            };
          });
          console.log('Mapped age categories:', mapped); // Debugging
          setAgeCategories(mapped);
          setAgeCategoryTotalPages(data?.totalPages || 1);
          setAgeCategoryTotalCount(data?.totalCount || 0);
          setAgeCategoriesLoading(false);
        })
        .catch(() => {
          setAgeCategoriesError('Failed to load age categories.');
          setAgeCategoriesLoading(false);
        });
    }
  }, [activeKey, tournament, ageCategoryPage, ageCategoryPageSize, ageCategorySearch, ageCategoryRefresh]);

  // Update options based on actual age categories data
  useEffect(() => {
    if (ageCategories.length > 0) {
      // Use Array.from instead of spread operator to avoid Set iteration issues
      const standingsTypes = ageCategories.map((ac: any) => ac.standingsType).filter(Boolean);
      const formats = ageCategories.map((ac: any) => ac.format).filter(Boolean);
      
      const uniqueStandingsTypes = Array.from(new Set(standingsTypes));
      const uniqueFormats = Array.from(new Set(formats));
      
      setTypeOptions(prev => {
        const existingValues = prev.map(opt => opt.value);
        const newOptions = uniqueStandingsTypes
          .filter(type => !existingValues.includes(type))
          .map(type => ({ value: type, label: type.charAt(0).toUpperCase() + type.slice(1) }));
        return [...prev, ...newOptions];
      });
      
      setFormatOptions(prev => {
        const existingValues = prev.map(opt => opt.value);
        const newOptions = uniqueFormats
          .filter(format => !existingValues.includes(format))
          .map(format => ({ value: format, label: format.charAt(0).toUpperCase() + format.slice(1) }));
        return [...prev, ...newOptions];
      });
    }
  }, [ageCategories]);

  // Reset to page 1 on age category search change
  useEffect(() => {
    setAgeCategoryPage(1);
  }, [ageCategorySearch]);

  // Handler to refresh teams after add/edit/delete
  const refreshTeams = () => setTeamModalRefresh(v => !v);
  
  // Handler to refresh age categories after add/edit/delete
  const refreshAgeCategories = () => {
    console.log('Refreshing age categories...');
    setAgeCategoryRefresh(v => !v);
  };

  // Fetch teams and age categories for dropdowns (only once per tournament)
  useEffect(() => {
    if (tournament && (tournament._id)) {
      // Teams
      listTeams({ 
        page: 1, 
        pageSize: 100, 
        tournamentId: tournament._id,
        sport: tournament.sports || getSportValueUtil(tournament, { state: window.history.state?.usr, search: window.location.search }) // FIX: Pass sport parameter
      })
        .then(data => {
          const arr = data?.paginatedResults || [];
          setTeamOptions(arr.map((t: any) => ({ value: t._id || t.id, label: t.name })));
        });
      // Age categories
      getAllAgeCategoriesPaginated(tournament._id, 1, 100, '', '', '', undefined, tournament.sports || 'Football')
        .then(data => {
          const arr = data?.paginatedResults || [];
          setAgeCategoryOptions(arr.map((ac: any) => ({ value: ac.ageCategory || ac._id, label: ac.ageCategory })));
        });
    }
  }, [tournament]);

  // Fetch players (with search & pagination)
  const fetchPlayers = React.useCallback(() => {
    if (!tournament || !(tournament._id)) return;
    setPlayersLoading(true);
    setPlayersError(null);
    listPlayers({
      search: playerSearch,
      page: playersPage,
      pageSize: playersPageSize,
      tournament: tournament._id,
      team: playersTeamFilter ? playersTeamFilter.value : '',
      ageCategory: playersAgeCategoryFilter?.label || '',
      sport: tournament.sports || 'Football' // FIX: Pass sport parameter
    })
      .then(data => {
        const arr = data?.paginatedResults || [];
        // Map API response to PlayersTable expected structure
        const mappedPlayers = arr.map((p: any) => ({
          ...p,
          teamInfo: p.team ? { name: p.team.name, shortName: p.team.shortName } : { name: '', shortName: '' },
          ageCategory: p.ageCategory || '',
        }));
        setPlayers(mappedPlayers);
        setPlayersTotalPages(data?.totalPages || 1);
        setPlayersTotalCount(data?.totalCount || 0);
      })
      .catch(() => setPlayersError('Failed to load players.'))
      .finally(() => setPlayersLoading(false));
  }, [tournament, playerSearch, playersPage, playersPageSize, playersTeamFilter, playersAgeCategoryFilter, playersRefresh]);

  useEffect(() => {
    if (activeKey === 'players') {
      fetchPlayers();
    }
  }, [activeKey, fetchPlayers]);

  // Fetch standings (with search & pagination)
  const fetchStandings = React.useCallback(() => {
    if (!tournament || !(tournament._id)) return;
    setStandingsLoading(true);
    setStandingsError(null);
    
    // Get sport for API call
    const sport = tournament?.sports || getSportValueUtil(tournament, { state: window.history.state?.usr, search: window.location.search }) || 'Football';
    
    getAllStandings(tournament._id, {
      search: standingsSearch,
      page: standingsPage,
      pageSize: standingsPageSize,
      ageCategory: standingsAgeCategoryFilter?.value || '',
      group: standingsGroupFilter?.value || '',
    }, sport)
      .then(data => {
        const arr = data?.paginatedResults || [];
        setStandings(arr);
        setStandingsTotalPages(data?.totalPages || 1);
        setStandingsTotalCount(data?.totalCount || 0);
      })
      .catch(() => setStandingsError('Failed to load standings.'))
      .finally(() => setStandingsLoading(false));
  }, [tournament, standingsSearch, standingsPage, standingsPageSize, standingsAgeCategoryFilter, standingsGroupFilter, standingsRefresh]);

  useEffect(() => {
    if (activeKey === 'standings') {
      fetchStandings();
    }
  }, [activeKey, fetchStandings]);

  // Handlers for add/edit/delete
  const handleAddPlayer = React.useCallback(async (payload: any) => {
    console.log('handleAddPlayer called with payload:', payload);
    console.log('addPlayer function:', addPlayer);
    try {
      const sport = tournament?.sports || getSportValueUtil(tournament, {});
      await addPlayer(payload, sport);
      console.log('addPlayer API call completed successfully');
      setPlayersRefresh(v => !v);
    } catch (error) {
      console.error('Error in handleAddPlayer:', error);
    }
  }, [tournament]);
  const handleEditPlayer = React.useCallback(async (payload: any) => {
    console.log('handleEditPlayer called with payload:', payload);
    console.log('editPlayer function:', editPlayer);
    try {
      const sport = tournament?.sports || getSportValueUtil(tournament, {});
      await editPlayer(payload._id, payload, sport);
      console.log('editPlayer API call completed successfully');
      setPlayersRefresh(v => !v);
    } catch (error) {
      console.error('Error in handleEditPlayer:', error);
    }
  }, [tournament]);
  const handleDeletePlayer = React.useCallback(async (playerId: string, sport: string) => {
    await deletePlayer(playerId, sport);
    setPlayersRefresh(v => !v);
  }, []);

  // Handlers for standings add/edit/delete
  const handleAddStanding = React.useCallback(async () => {
    console.log('handleAddStanding called');
    setStandingsRefresh(v => !v);
  }, []);
  const handleEditStanding = React.useCallback(async () => {
    console.log('handleEditStanding called');
    setStandingsRefresh(v => !v);
  }, []);
  const handleDeleteStanding = React.useCallback(async () => {
    console.log('handleDeleteStanding called');
    setStandingsRefresh(v => !v);
  }, []);

  // Reset to page 1 on search change
  useEffect(() => {
    setPlayersPage(1);
  }, [playerSearch, playersTeamFilter]);

  // Reset to page 1 on standings search/filter change
  useEffect(() => {
    setStandingsPage(1);
  }, [standingsSearch, standingsAgeCategoryFilter, standingsGroupFilter]);

  const sportOptions = [
    { value: 'Football', label: 'Football' },
    { value: 'Tennis', label: 'Tennis' },
    { value: 'Pickleball', label: 'Pickleball' },
  ];

  const locationOptions = [
    { value: 'Mumbai', label: 'Mumbai' },
    { value: 'Navi Mumbai', label: 'Navi Mumbai' },
    { value: 'Pune', label: 'Pune' },
    { value: 'Thane', label: 'Thane' },
  ];

  const statusOptions = [
    { value: 'ongoing', label: 'Ongoing' },
    { value: 'completed', label: 'Completed' },
    { value: 'upcoming', label: 'Upcoming' },
  ];

  // Helper to format date for input fields
  const formatDateForInput = (dateStr: string) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toISOString().slice(0, 10);
  };

  const handleEdit = () => {
    if (!tournament) return;
    setSuccess(null);
    setError(null);
    // Map sport to react-select option
    let sportValue = null;
    if (tournament.sports) {
      sportValue = sportOptions.find(opt => opt.value === tournament.sports);
    }
    // In handleEdit, prefill location as a react-select option
    let locationValue = null;
    if (tournament.location) {
      locationValue = locationOptions.find(opt => opt.value === tournament.location);
    }
    // Map status to react-select option
    let statusValue = null;
    if (tournament.status) {
      statusValue = statusOptions.find(opt => opt.value === tournament.status);
    }
    const editForm = {
      ...tournament,
      sport: sportValue,
      location: locationValue,
      status: statusValue,
      startDate: formatDateForInput(tournament.startDate),
      endDate: formatDateForInput(tournament.endDate),
    };
    setForm(editForm);
    setOriginalEditValues(editForm);
    setFormTouched({});
    setShowEditModal(true);
  };
  const handleCancel = () => {
    setShowEditModal(false);
    setForm(tournament);
  };
  const validateForm = () => {
    return {
      name: !form.name?.trim() ? 'Name is required.' : '',
      shortName: !form.shortName?.trim() ? 'Abbreviation is required.' : '',
      location: !form.location || !form.location.value ? 'Location is required.' : '',
      sport: !form.sport ? 'Sport is required.' : '',
      startDate: !form.startDate ? 'Start Date is required.' : '',
      endDate: !form.endDate ? 'End Date is required.' : '',
      status: !form.status ? 'Status is required.' : '',
    };
  };
  const errors = validateForm();
  const isFormValid = Object.values(errors).every(x => !x);

  // Helper to check if any field has changed
  function isFormChanged(form: any, original: any) {
    if (!original) return false;
    return (
      form.name !== original.name ||
      form.shortName !== original.shortName ||
      (form.location && original.location && form.location.value !== original.location.value) ||
      (form.sport && original.sport && form.sport.value !== original.sport.value) ||
      form.startDate !== original.startDate ||
      form.endDate !== original.endDate ||
      form.status !== original.status ||
      form.logo !== original.logo
    );
  }

  const handleSave = async () => {
    if (!tournament) return;
    setFormTouched({
      name: true,
      shortName: true,
      location: true,
      sport: true,
      startDate: true,
      endDate: true,
      status: true,
    });
    if (!isFormValid) return;
    setFormError(null);
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await editTournament({
        ...form,
        location: form.location?.value || '',
        sport: undefined,
        sports: form.sport ? form.sport.value : '',
        status: form.status ? form.status.value : '',
        _id: tournament._id
      });
      // Refetch tournament details from backend
      if (id) {
        // Get sport from form data, location state, or URL search params
        // Sport is REQUIRED for tournament management module
        const sportFromForm = form.sport?.value || form.sports;
        const urlParams = new URLSearchParams(window.location.search);
        const sportFromUrl = urlParams.get('sport');
        const selectedTournamentFromState = (window.history.state?.usr as any)?.selectedTournament;
        const sportFromState = selectedTournamentFromState?.sports;
        // Use shared utility to get sport - throws error if not available
        const sport = sportFromForm || sportFromState || sportFromUrl || tournament?.sports || getSportValueUtil(tournament, { state: window.history.state?.usr, search: window.location.search });
        
        const updated = await getTournamentById(id, sport);
        setTournament(updated);
      }
      setShowEditModal(false);
      setSuccess('Tournament updated successfully.');
    } catch (err: any) {
      // Try to display field-specific error if present
      if (err.response && err.response.data && err.response.data.errors) {
        const errors = err.response.data.errors;
        // Set errors on the form fields
        setFormError(null);
        setError(null);
        setFormTouched((prev: any) => ({ ...prev, ...Object.keys(errors).reduce((acc, key) => ({ ...acc, [key]: true }), {}) }));
        // Optionally, you can set a fieldErrors state to display errors next to fields
        // For now, show the first error as a generic error
        setFormError(String(Object.values(errors)[0]));
      } else if (err.response && err.response.data && err.response.data.message) {
        setFormError(err.response.data.message);
      } else {
        setFormError('Failed to update tournament.');
      }
    } finally {
      setSaving(false);
    }
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setFormTouched({ ...formTouched, [e.target.name]: true });
  };
  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setFormTouched({ ...formTouched, [e.target.name]: true });
  };
  const handleSportChange = (option: any) => {
    setForm({ ...form, sport: option });
    setFormTouched({ ...formTouched, sport: true });
  };

  const handleLocationChange = (option: any) => {
    setForm({ ...form, location: option });
    setFormTouched({ ...formTouched, location: true });
  };

  const handleStatusChange = (option: any) => {
    setForm({ ...form, status: option });
    setFormTouched({ ...formTouched, status: true });
  };

  const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoUploading(true);
    setLogoError(null);
    try {
      const s3Key = await uploadTournamentLogo(file);
      setForm((prev: any) => ({ ...prev, logo: s3Key }));
    } catch (err) {
      setLogoError('Failed to upload logo.');
    } finally {
      setLogoUploading(false);
    }
  };

  // Handler for editing an age category
  const handleEditAgeCategory = (idx: number) => {
    setEditAgeCategoryIndex(idx);
    setShowAgeCategoryModal(true);
  };
  // Handler for deleting an age category
  const handleDeleteAgeCategory = async (idx: number) => {
    if (!ageCategories[idx]) return;
    setAgeCategoriesLoading(true);
    try {
      // Assuming deleteAgeCategory is an API call or function that handles deletion
      // For now, we'll just remove it from the state and refresh the list
      const updatedAgeCategories = ageCategories.filter((_, i) => i !== idx);
      setAgeCategories(updatedAgeCategories);
      setAgeCategoryTotalCount(ageCategoryTotalCount - 1); // Decrement total count
      setAgeCategoryTotalPages(Math.ceil((ageCategoryTotalCount - 1) / ageCategoryPageSize)); // Recalculate total pages
    } catch (err) {
      setAgeCategoriesError('Failed to delete age category.');
    } finally {
      setAgeCategoriesLoading(false);
    }
  };

  if (!tournament && loading) {
    return (
      <div className="container-fluid">
        <div className="row">
          <div className="col-md-6">
            <ShimmerLoader type="form" />
          </div>
          <div className="col-md-6">
            <ShimmerLoader type="image" />
          </div>
        </div>
        <div className="mt-4">
          <ShimmerLoader type="table" rows={5} />
        </div>
      </div>
    );
  }

  const tabItems = [
    { key: 'details', label: 'Details' },
    { key: 'age-category', label: 'Age Category' },
    { key: 'teams', label: 'Teams' },
    { key: 'players', label: 'Players' },
    { key: 'standings', label: 'Standings' },
    { key: 'voting', label: 'Voting' },
    { key: 'predictions', label: 'Predictions' },
  ];

  if (!tournament) {
    return <div>Loading...</div>;
  }

  return (
    <div className="container-fluid">
      <Breadcrumbs items={[
        { label: 'Tournaments', onClick: () => navigate('/tournament-details') },
        { label: tournament.name, active: true }
      ]} />
      <Modal show={showEditModal} onHide={handleCancel}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Tournament</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <div className="alert alert-danger">{error}</div>}
          {success && <div className="alert alert-success">{success}</div>}
          {formError && <div className="alert alert-danger">{formError}</div>}
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Name<span style={{ color: 'red' }}>*</span></Form.Label>
              <Form.Control name="name" value={form.name} onChange={handleInputChange} isInvalid={!!errors.name && formTouched.name} onBlur={() => setFormTouched({ ...formTouched, name: true })} />
              {errors.name && formTouched.name && <div className="text-danger">{errors.name}</div>}
            </Form.Group>
            <Form.Group className="mb-3">
                              <Form.Label>Abbreviation<span style={{ color: 'red' }}>*</span></Form.Label>
              <Form.Control name="shortName" value={form.shortName} onChange={handleInputChange} isInvalid={!!errors.shortName && formTouched.shortName} onBlur={() => setFormTouched({ ...formTouched, shortName: true })} />
              {errors.shortName && formTouched.shortName && <div className="text-danger">{errors.shortName}</div>}
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Location<span style={{ color: 'red' }}>*</span></Form.Label>
              <Select
                classNamePrefix="react-select"
                name="location"
                value={form.location}
                onChange={handleLocationChange}
                options={locationOptions}
                placeholder="Select Location"
                isClearable={false}
                styles={{ control: base => ({ ...base, minHeight: '38px' }) }}
              />
              {errors.location && formTouched.location && <div className="text-danger">{errors.location}</div>}
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Sport<span style={{ color: 'red' }}>*</span></Form.Label>
              <Select
                classNamePrefix="react-select"
                name="sport"
                value={form.sport}
                onChange={handleSportChange}
                options={sportOptions}
                placeholder="Select Sport"
                isClearable={false}
                styles={{ control: base => ({ ...base, minHeight: '38px' }) }}
              />
              {errors.sport && formTouched.sport && <div className="text-danger">{errors.sport}</div>}
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Start Date<span style={{ color: 'red' }}>*</span></Form.Label>
              <Form.Control type="date" name="startDate" value={form.startDate} onChange={handleInputChange} isInvalid={!!errors.startDate && formTouched.startDate} onBlur={() => setFormTouched({ ...formTouched, startDate: true })} />
              {errors.startDate && formTouched.startDate && <div className="text-danger">{errors.startDate}</div>}
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>End Date<span style={{ color: 'red' }}>*</span></Form.Label>
              <Form.Control type="date" name="endDate" value={form.endDate} onChange={handleInputChange} isInvalid={!!errors.endDate && formTouched.endDate} onBlur={() => setFormTouched({ ...formTouched, endDate: true })} />
              {errors.endDate && formTouched.endDate && <div className="text-danger">{errors.endDate}</div>}
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Status<span style={{ color: 'red' }}>*</span></Form.Label>
              <Select
                classNamePrefix="react-select"
                name="status"
                value={form.status}
                onChange={handleStatusChange}
                options={statusOptions}
                placeholder="Select Status"
                isClearable={false}
                styles={{ control: base => ({ ...base, minHeight: '38px' }) }}
              />
              {errors.status && formTouched.status && <div className="text-danger">{errors.status}</div>}
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Logo</Form.Label>
              <Form.Control type="file" accept="image/*" onChange={handleLogoChange} disabled={logoUploading} />
              {logoUploading && <div>Uploading...</div>}
              {logoError && <div className="text-danger">{logoError}</div>}
              {form.logo && <img src={getImageUrl(form.logo)} alt="Logo Preview" style={{ width: 60, marginTop: 8 }} />}
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancel} disabled={saving}>Cancel</Button>
          <Button variant="primary" onClick={handleSave} disabled={!isFormValid || saving || !isFormChanged(form, originalEditValues)}>{saving ? 'Saving...' : 'Save'}</Button>
        </Modal.Footer>
      </Modal>
      <CustomTabs
        tabs={tabItems}
        activeKey={activeKey}
        onSelect={setActiveKey}
        orientation="horizontal"
      />
      <div style={{ marginTop: 24 }}>
        {activeKey === 'details' && (
          <div className="card tournament-details-card mb-4">
            <div className="card-body">
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3"><strong>Name:</strong> {tournament.name}</div>
                  <div className="mb-3"><strong>Abbreviation:</strong> {tournament.shortName}</div>
                  <div className="mb-3"><strong>Location:</strong> {tournament.location}</div>
                  <div className="mb-3"><strong>Sport:</strong> {tournament.sports ? <span className="custom-badge custom-badge-green" style={{ marginLeft: 8 }}>{tournament.sports}</span> : '-'}</div>
                  <div className="mb-3"><strong>Status:</strong> {tournament.status ? <span className={`custom-badge custom-badge-${tournament.status === 'ongoing' ? 'green' : tournament.status === 'completed' ? 'gray' : 'yellow'}`} style={{ marginLeft: 8 }}>{tournament.status.charAt(0).toUpperCase() + tournament.status.slice(1)}</span> : '-'}</div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3"><strong>Start Date:</strong> {tournament.startDate ? format(new Date(tournament.startDate), 'dd-MM-yyyy') : ''}</div>
                  <div className="mb-3"><strong>End Date:</strong> {tournament.endDate ? format(new Date(tournament.endDate), 'dd-MM-yyyy') : ''}</div>
                  {tournament.logo && (
                    <div className="mb-3">
                      <strong>Logo:</strong><br />
                      <img src={getImageUrl(tournament.logo)} alt="Tournament Logo" style={{ width: 80, height: 80, objectFit: 'cover', border: '2px solid #eee', borderRadius: 8 }} />
                    </div>
                  )}
                </div>
              </div>
              <Button variant="outline-primary" onClick={handleEdit} style={{ minWidth: 80, height: 40 }}>Edit</Button>
            </div>
          </div>
        )}
        {activeKey === 'age-category' && (
          ageCategoriesError ? (
            <div className="alert alert-danger">{ageCategoriesError}</div>
          ) : (
            <AgeCategoryTable
              ageCategories={ageCategories}
              search={ageCategorySearch}
              onSearch={setAgeCategorySearch}
              onAdd={refreshAgeCategories}
              onEdit={refreshAgeCategories}
              onDelete={refreshAgeCategories}
              tournamentId={tournament._id}
              ageCategoriesLoading={ageCategoriesLoading}
              page={ageCategoryPage}
              pageSize={ageCategoryPageSize}
              totalPages={ageCategoryTotalPages}
              totalCount={ageCategoryTotalCount}
              onPageChange={setAgeCategoryPage}
              onPageSizeChange={size => { setAgeCategoryPageSize(size); setAgeCategoryPage(1); }}
              typeFilter={typeFilter}
              setTypeFilter={setTypeFilter}
              formatFilter={formatFilter}
              setFormatFilter={setFormatFilter}
              typeOptions={typeOptions}
              formatOptions={formatOptions}
              tournamentSports={tournament.sports}
            />
          )
        )}
        {activeKey === 'teams' && (
          teamsError ? (
            <div className="alert alert-danger">{teamsError}</div>
          ) : (
            <TeamsTable
              teams={teams}
              search={teamSearch}
              onSearch={setTeamSearch}
              onAdd={refreshTeams}
              onEdit={refreshTeams}
              onDelete={refreshTeams}
              ageCategories={ageCategories}
              tournamentId={tournament._id}
              teamsLoading={teamsLoading}
              page={teamsPage}
              pageSize={teamsPageSize}
              totalPages={teamsTotalPages}
              totalCount={teamsTotalCount}
              onPageChange={setTeamsPage}
              onPageSizeChange={size => { setTeamsPageSize(size); setTeamsPage(1); }}
              ageCategoryFilter={teamsAgeCategoryFilter}
              onAgeCategoryFilterChange={setTeamsAgeCategoryFilter}
              tournamentSports={tournament.sports}
            />
          )
        )}
        {activeKey === 'players' && (
          <PlayersTable
            players={players}
            search={playerSearch}
            onSearch={setPlayerSearch}
            ageCategoryFilter={playersAgeCategoryFilter}
            onAgeCategoryFilterChange={setPlayersAgeCategoryFilter}
            onAdd={handleAddPlayer}
            onEdit={handleEditPlayer}
            onDelete={handleDeletePlayer}
            teams={teamOptions}
            ageCategories={ageCategoryOptions}
            loading={playersLoading}
            page={playersPage}
            pageSize={playersPageSize}
            totalPages={playersTotalPages}
            totalCount={playersTotalCount}
            onPageChange={setPlayersPage}
            onPageSizeChange={size => { setPlayersPageSize(size); setPlayersPage(1); }}
            tournamentId={tournament._id}
          />
        )}
        {activeKey === 'standings' && (
          standingsError ? (
            <div className="alert alert-danger">{standingsError}</div>
          ) : (
            <StandingsTable
              standings={standings}
              search={standingsSearch}
              onSearch={setStandingsSearch}
              ageCategoryFilter={standingsAgeCategoryFilter}
              onAgeCategoryFilterChange={setStandingsAgeCategoryFilter}
              groupFilter={standingsGroupFilter}
              onGroupFilterChange={setStandingsGroupFilter}
              onAdd={handleAddStanding}
              onEdit={handleEditStanding}
              onDelete={handleDeleteStanding}
              ageCategories={ageCategoryOptions}
              tournamentId={tournament._id}
              standingsLoading={standingsLoading}
              page={standingsPage}
              pageSize={standingsPageSize}
              totalPages={standingsTotalPages}
              totalCount={standingsTotalCount}
              onPageChange={setStandingsPage}
              onPageSizeChange={size => { setStandingsPageSize(size); setStandingsPage(1); }}
              tournamentSports={tournament.sports}
            />
          )
        )}
        {activeKey === 'voting' && (
          <VotingManagement
            tournamentId={tournament?._id}
            seasonId={defaultSeasonId}
            tournamentSports={tournament.sports}
          />
        )}
        {activeKey === 'predictions' && (
          <PredictionsManagement
            tournamentId={tournament?._id}
            seasonId={defaultSeasonId}
            tournamentSports={tournament.sports}
          />
        )}
      </div>
    </div>
  );
};

export default TournamentDetailsPage; 
